import React from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { MediaUploadField } from '@modules/ui-components';
import { ColorInput, isValidHexColor } from '@modules/reactFlow/components/shared/styleFields';
import type { AiAssetReference, DesignSystemProfile, PlatformDesignProfile } from '@modules/content-plan/types';
import { emptyBrandStyleKitDraft } from './BrandStyleKitDraft';
import type {
  BrandStyleKitDraft,
  BrandStyleKitReferenceDraft,
  BrandStyleKitReferenceTextBudgetDraft,
} from './BrandStyleKitDraft';
import { ReferenceSetImportDialog, type ReferenceSetImportItem } from './ReferenceSetImportDialog';

export const BRAND_STYLE_REFERENCE_LIMIT = 14;

export const DESIGN_PROFILE_OPTIONS = [
  {
    value: 'design',
    label: 'Default brand system',
    helper: 'Shared defaults inherited by format-specific profiles.',
  },
  {
    value: 'single_image_landscape',
    label: 'Single image - landscape',
    helper: '1.91:1 image posts and link-preview-like visuals.',
  },
  {
    value: 'single_image_square',
    label: 'Single image - square',
    helper: '1:1 single image posts.',
  },
  {
    value: 'single_image_portrait',
    label: 'Single image - portrait',
    helper: '4:5 single image posts when enabled for the account.',
  },
  {
    value: 'carousel_square',
    label: 'Carousel - square',
    helper: '1:1 multi-slide posts. Use references for cover, body, and CTA slide patterns.',
  },
  {
    value: 'carousel_portrait',
    label: 'Carousel - portrait',
    helper: '4:5 multi-slide posts. Use references for cover, body, and CTA slide patterns.',
  },
] as const;

export type BrandStyleKitProfileKey = typeof DESIGN_PROFILE_OPTIONS[number]['value'];

export { emptyBrandStyleKitDraft };
export type { BrandStyleKitDraft, BrandStyleKitReferenceDraft };

export const REFERENCE_ROLES: Array<{ value: BrandStyleKitReferenceDraft['role']; label: string }> = [
  { value: 'style', label: 'Style' },
  { value: 'layout', label: 'Layout' },
  { value: 'typography', label: 'Typography' },
  { value: 'logo', label: 'Logo' },
  { value: 'approved', label: 'Approved' },
  { value: 'motif', label: 'Motif' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'object', label: 'Object' },
  { value: 'character', label: 'Character' },
];

const IMAGE_TREATMENT_OPTIONS = [
  { value: '', label: 'Inherit' },
  { value: 'photographic', label: 'Photographic' },
  { value: 'flat_vector', label: 'Flat vector' },
  { value: '3d_clay', label: '3D clay' },
  { value: 'hand_drawn', label: 'Hand drawn' },
  { value: 'brutalist', label: 'Brutalist' },
];

const MOTION_OPTIONS = [
  { value: '', label: 'Inherit' },
  { value: 'none', label: 'None' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'expressive', label: 'Expressive' },
];

const TEXT_POLICY_OPTIONS = [
  { value: 'render_in_model', label: 'Render in model' },
  { value: 'mixed', label: 'Mixed' },
];

const DEFAULT_TEXT_POLICY_MODE = 'render_in_model';

function normalizeEditorTextPolicyMode(mode: unknown): string {
  return TEXT_POLICY_OPTIONS.some((option) => option.value === mode)
    ? String(mode)
    : DEFAULT_TEXT_POLICY_MODE;
}

export function profileToBrandStyleKitDraft(profile: PlatformDesignProfile | null | undefined): BrandStyleKitDraft {
  if (!profile) return emptyBrandStyleKitDraft();
  const designSystem = profile.designSystem ?? {};
  const visualStyle = designSystem.visualStyle ?? {};
  const composition = designSystem.composition ?? {};
  const illustration = designSystem.illustrationStyle ?? {};
  const photo = designSystem.photoStyle ?? {};
  const textPolicy = designSystem.textPolicy ?? {};
  return {
    designDescription: profile.designDescription ?? '',
    primaryColor: designSystem.palette?.primary ?? '',
    accentColor: designSystem.palette?.accent ?? '',
    neutralColors: (designSystem.palette?.neutral ?? []).join('\n'),
    forbiddenColors: (designSystem.palette?.forbidden ?? []).join('\n'),
    primaryFontName: designSystem.typography?.primaryFontName ?? '',
    headingStyle: designSystem.typography?.headingStyle ?? '',
    bodyStyle: designSystem.typography?.bodyStyle ?? '',
    moodKeywords: (visualStyle.moodKeywords ?? []).join('\n'),
    imageTreatment: illustration.type ?? visualStyle.illustrationStyle ?? '',
    motionCulture: visualStyle.motionCulture ?? '',
    compositionGrid: composition.grid ?? '',
    compositionSpacing: composition.spacing ?? '',
    compositionHierarchy: composition.hierarchy ?? '',
    illustrationLineWeight: illustration.lineWeight ?? '',
    illustrationShadow: illustration.shadow ?? '',
    illustrationTexture: illustration.texture ?? '',
    photoCamera: photo.camera ?? '',
    photoLighting: photo.lighting ?? '',
    photoBackground: photo.background ?? '',
    brandMotifs: (designSystem.brandMotifs ?? []).join('\n'),
    forbiddenVisuals: (profile.forbiddenVisuals ?? []).join('\n'),
    textPolicyMode: normalizeEditorTextPolicyMode(textPolicy.mode),
    exactTextRequired: textPolicy.exactTextRequired === true || !textPolicy.mode,
    references: referencesFromProfile(profile),
    isComplete: profile.isComplete === true,
  };
}

export function brandStyleKitDraftToPatch(
  platformId: string,
  draft: BrandStyleKitDraft,
  source: PlatformDesignProfile['source'] = 'manual',
): Partial<PlatformDesignProfile> {
  const designSystem = stripEmptyObject({
    palette: stripEmptyObject({
      primary: draft.primaryColor.trim() || undefined,
      accent: draft.accentColor.trim() || undefined,
      neutral: lines(draft.neutralColors),
      forbidden: lines(draft.forbiddenColors),
    }),
    typography: stripEmptyObject({
      primaryFontName: draft.primaryFontName.trim() || undefined,
      headingStyle: draft.headingStyle || undefined,
      bodyStyle: draft.bodyStyle || undefined,
    }),
    visualStyle: stripEmptyObject({
      moodKeywords: lines(draft.moodKeywords),
      illustrationStyle: draft.imageTreatment || undefined,
      motionCulture: draft.motionCulture || undefined,
    }),
    composition: stripEmptyObject({
      grid: draft.compositionGrid.trim() || undefined,
      spacing: draft.compositionSpacing.trim() || undefined,
      hierarchy: draft.compositionHierarchy.trim() || undefined,
    }),
    illustrationStyle: stripEmptyObject({
      type: draft.imageTreatment || undefined,
      lineWeight: draft.illustrationLineWeight.trim() || undefined,
      shadow: draft.illustrationShadow.trim() || undefined,
      texture: draft.illustrationTexture.trim() || undefined,
    }),
    photoStyle: stripEmptyObject({
      camera: draft.photoCamera.trim() || undefined,
      lighting: draft.photoLighting.trim() || undefined,
      background: draft.photoBackground.trim() || undefined,
    }),
    brandMotifs: lines(draft.brandMotifs),
    textPolicy: stripEmptyObject({
      mode: normalizeEditorTextPolicyMode(draft.textPolicyMode),
      exactTextRequired: draft.exactTextRequired,
    }),
  }) as DesignSystemProfile | undefined;

  return {
    platformId,
    designDescription: draft.designDescription.trim() || undefined,
    designSystem,
    forbiddenVisuals: lines(draft.forbiddenVisuals),
    visualReferences: draft.references
      .map((ref) => ({
        url: ref.url.trim(),
        mimeType: ref.mimeType || inferBrandStyleKitMimeType(ref.url),
        name: ref.name.trim() || undefined,
        instruction: ref.instruction.trim() || undefined,
        role: ref.role,
        textBudgets: textBudgetDraftsToReference(ref.textBudgets ?? []),
      }))
      .filter((ref) => ref.url) as AiAssetReference[],
    source,
    isComplete: draft.isComplete,
  };
}

export function inferBrandStyleKitMimeType(url: string): string {
  const clean = url.split('?')[0].toLowerCase();
  if (clean.endsWith('.jpg') || clean.endsWith('.jpeg')) return 'image/jpeg';
  if (clean.endsWith('.webp')) return 'image/webp';
  return 'image/png';
}

export function getDesignProfileOption(profileKey: string) {
  return DESIGN_PROFILE_OPTIONS.find((option) => option.value === profileKey) ?? DESIGN_PROFILE_OPTIONS[0];
}

export function isFormatSpecificDesignProfile(profileKey: string): boolean {
  return profileKey !== 'design';
}

export type BrandStyleKitEditorProps = {
  draft: BrandStyleKitDraft;
  onChange: (nextDraft: BrandStyleKitDraft) => void;
  profileKey: string;
  platformId: string;
  disabled?: boolean;
  uploadingReferenceIndex?: number | null;
  onReferenceMediaChange: (index: number, value: string | File | null) => void | Promise<void>;
  showReviewed?: boolean;
  showReferences?: boolean;
  showDesignBrief?: boolean;
  testIdPrefix?: string;
};

export function BrandStyleKitScopeSelector({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, nextValue) => {
        if (nextValue) onChange(nextValue);
      }}
      disabled={disabled}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 1,
        '& .MuiToggleButtonGroup-grouped': {
          borderRadius: 1,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          m: 0,
        },
      }}
    >
      {DESIGN_PROFILE_OPTIONS.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            p: 1.25,
            textAlign: 'left',
            textTransform: 'none',
          }}
        >
          <Stack spacing={0.25} alignItems="flex-start">
            <Typography variant="subtitle2">{option.label}</Typography>
            <Typography variant="caption" color="text.secondary">{option.helper}</Typography>
          </Stack>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

export const BrandStyleKitEditor: React.FC<BrandStyleKitEditorProps> = ({
  draft,
  onChange,
  profileKey,
  platformId,
  disabled = false,
  uploadingReferenceIndex = null,
  onReferenceMediaChange,
  showReviewed = true,
  showReferences = true,
  showDesignBrief = true,
  testIdPrefix = `platform-${platformId}-${profileKey}-design`,
}) => {
  const savedReferenceCount = React.useMemo(
    () => draft.references.filter((ref) => ref.url.trim()).length,
    [draft.references],
  );
  const remainingReferenceSlots = Math.max(0, BRAND_STYLE_REFERENCE_LIMIT - draft.references.length);
  const [referenceSetOpen, setReferenceSetOpen] = React.useState(false);
  const [referenceSetImporting, setReferenceSetImporting] = React.useState(false);
  const profileOption = getDesignProfileOption(profileKey);

  const update = (patch: Partial<BrandStyleKitDraft>) => {
    onChange({ ...draft, ...patch });
  };

  const updateReference = (index: number, patch: Partial<BrandStyleKitReferenceDraft>) => {
    onChange({
      ...draft,
      references: draft.references.map((ref, refIndex) => (
        refIndex === index ? { ...ref, ...patch } : ref
      )),
    });
  };

  const updateReferenceTextBudget = (
    referenceIndex: number,
    budgetIndex: number,
    patch: Partial<BrandStyleKitReferenceTextBudgetDraft>,
  ) => {
    const reference = draft.references[referenceIndex];
    if (!reference) return;
    updateReference(referenceIndex, {
      textBudgets: (reference.textBudgets ?? []).map((budget, currentIndex) => (
        currentIndex === budgetIndex ? { ...budget, ...patch } : budget
      )),
    });
  };

  const addReferenceTextBudget = (referenceIndex: number) => {
    const reference = draft.references[referenceIndex];
    if (!reference) return;
    const textBudgets = reference.textBudgets ?? [];
    updateReference(referenceIndex, {
      textBudgets: [
        ...textBudgets,
        defaultReferenceTextBudgetDraft(textBudgets.length),
      ],
    });
  };

  const removeReferenceTextBudget = (referenceIndex: number, budgetIndex: number) => {
    const reference = draft.references[referenceIndex];
    if (!reference) return;
    updateReference(referenceIndex, {
      textBudgets: (reference.textBudgets ?? []).filter((_, currentIndex) => currentIndex !== budgetIndex),
    });
  };

  const removeReference = (index: number) => {
    onChange({
      ...draft,
      references: draft.references.filter((_, refIndex) => refIndex !== index),
    });
  };

  const addReference = () => {
    onChange({
      ...draft,
      references: [
        ...draft.references,
        defaultReferenceDraft(profileKey, draft.references.length),
      ],
    });
  };

  const importReferenceSet = async (items: ReferenceSetImportItem[]) => {
    const selected = items.slice(0, remainingReferenceSlots);
    if (selected.length === 0) return;
    const startIndex = draft.references.length;
    setReferenceSetImporting(true);
    onChange({
      ...draft,
      references: [
        ...draft.references,
        ...selected.map((item) => item.draft),
      ],
    });
    try {
      for (let index = 0; index < selected.length; index += 1) {
        await onReferenceMediaChange(startIndex + index, selected[index].file);
      }
      setReferenceSetOpen(false);
    } finally {
      setReferenceSetImporting(false);
    }
  };

  return (
    <Stack spacing={2.5} data-testid={`${testIdPrefix}-editor`}>
      <Alert severity={isFormatSpecificDesignProfile(profileKey) ? 'info' : 'success'}>
        Editing <strong>{profileOption.label}</strong>. {profileOption.helper}
      </Alert>

      {showReferences && (
        <EditorSection title={`Visual references (${savedReferenceCount}/${BRAND_STYLE_REFERENCE_LIMIT})`} defaultExpanded>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {referenceGuidance(profileKey)}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileUploadOutlinedIcon />}
                onClick={() => setReferenceSetOpen(true)}
                disabled={disabled || remainingReferenceSlots <= 0}
                data-testid={`${testIdPrefix}-reference-set-import`}
              >
                Import reference set
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addReference}
                disabled={disabled || draft.references.length >= BRAND_STYLE_REFERENCE_LIMIT}
              >
                Add reference
              </Button>
            </Stack>
          </Stack>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {draft.references.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Add URLs or upload images. The resolver keeps reference order for prompt instructions.
              </Typography>
            )}
            {draft.references.map((ref, index) => (
              <Box key={index}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                      <TextField
                        label="Reference name"
                        size="small"
                        value={ref.name}
                        onChange={(event) => updateReference(index, { name: event.target.value })}
                        disabled={disabled}
                        sx={{ flex: 1 }}
                        placeholder={referenceSlotHint(profileKey, index)}
                      />
                      <Box sx={{ flex: 2, minWidth: 0 }}>
                        <MediaUploadField
                          value={ref.url || null}
                          onChange={(value) => { void onReferenceMediaChange(index, value); }}
                          mediaType="image"
                          size="small"
                          showPreview
                          disabled={disabled || uploadingReferenceIndex === index}
                          placeholder={`Paste URL or upload ${ref.name || 'style reference'}`}
                          inputTestId={`${testIdPrefix}-reference-${index}`}
                          previewTestId={`${testIdPrefix}-reference-${index}-preview`}
                        />
                      </Box>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <SelectField
                        label="Reference role"
                        value={ref.role}
                        options={REFERENCE_ROLES}
                        onChange={(value) => updateReference(index, { role: value as BrandStyleKitReferenceDraft['role'] })}
                        disabled={disabled}
                        sx={{ minWidth: 180, flex: '0 0 auto' }}
                      />
                      <TextField
                        label="Agent instruction"
                        size="small"
                        multiline
                        minRows={2}
                        value={ref.instruction}
                        onChange={(event) => updateReference(index, { instruction: event.target.value })}
                        disabled={disabled}
                        placeholder={defaultReferenceInstruction(profileKey, index, ref.role)}
                        fullWidth
                      />
                    </Stack>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          Text budgets
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<AddIcon />}
                          onClick={() => addReferenceTextBudget(index)}
                          disabled={disabled}
                        >
                          Add budget
                        </Button>
                      </Stack>
                      {(ref.textBudgets ?? []).map((budget, budgetIndex) => (
                        <Stack
                          key={budgetIndex}
                          direction={{ xs: 'column', lg: 'row' }}
                          spacing={1}
                          alignItems={{ lg: 'flex-start' }}
                        >
                          <TextField
                            label="Slot"
                            size="small"
                            value={budget.slot}
                            onChange={(event) => updateReferenceTextBudget(index, budgetIndex, { slot: event.target.value })}
                            disabled={disabled}
                            sx={{ minWidth: 132 }}
                          />
                          <TextField
                            label="Text role"
                            size="small"
                            value={budget.textRole}
                            onChange={(event) => updateReferenceTextBudget(index, budgetIndex, { textRole: event.target.value })}
                            disabled={disabled}
                            sx={{ minWidth: 124 }}
                          />
                          <TextField
                            label="Container"
                            size="small"
                            value={budget.containerSlot}
                            onChange={(event) => updateReferenceTextBudget(index, budgetIndex, { containerSlot: event.target.value })}
                            disabled={disabled}
                            sx={{ minWidth: 140 }}
                          />
                          <TextField
                            label="Label"
                            size="small"
                            value={budget.label}
                            onChange={(event) => updateReferenceTextBudget(index, budgetIndex, { label: event.target.value })}
                            disabled={disabled}
                            sx={{ minWidth: 132 }}
                          />
                          <TextField
                            label="Recommended"
                            size="small"
                            type="number"
                            value={budget.recommendedChars}
                            onChange={(event) => updateReferenceTextBudget(index, budgetIndex, { recommendedChars: event.target.value })}
                            disabled={disabled}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: { xs: '100%', lg: 132 } }}
                          />
                          <TextField
                            label="Max chars"
                            size="small"
                            type="number"
                            value={budget.maxChars}
                            onChange={(event) => updateReferenceTextBudget(index, budgetIndex, { maxChars: event.target.value })}
                            disabled={disabled}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: { xs: '100%', lg: 120 } }}
                          />
                          <TextField
                            label="Max lines"
                            size="small"
                            type="number"
                            value={budget.maxLines}
                            onChange={(event) => updateReferenceTextBudget(index, budgetIndex, { maxLines: event.target.value })}
                            disabled={disabled}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: { xs: '100%', lg: 120 } }}
                          />
                          <TextField
                            label="Notes"
                            size="small"
                            value={budget.notes}
                            onChange={(event) => updateReferenceTextBudget(index, budgetIndex, { notes: event.target.value })}
                            disabled={disabled}
                            sx={{ flex: 1, minWidth: 160 }}
                          />
                          <IconButton
                            aria-label="Remove text budget"
                            onClick={() => removeReferenceTextBudget(index, budgetIndex)}
                            size="small"
                            disabled={disabled}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                    {ref.url && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          fontFamily: 'monospace',
                          fontSize: '0.65rem',
                          wordBreak: 'break-all',
                        }}
                      >
                        {ref.url}
                      </Typography>
                    )}
                  </Stack>
                  <IconButton
                    aria-label="Remove reference"
                    onClick={() => removeReference(index)}
                    size="small"
                    disabled={disabled}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                {index < draft.references.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))}
          </Stack>
        </EditorSection>
      )}

      <EditorSection title="Core style" defaultExpanded>
        <Stack spacing={2}>
          {showDesignBrief && (
            <TextField
              label="Design brief"
              multiline
              minRows={4}
              value={draft.designDescription}
              onChange={(event) => update({ designDescription: event.target.value })}
              fullWidth
              disabled={disabled}
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <SingleColorField
              label="Primary color"
              value={draft.primaryColor}
              fallback="#112233"
              onChange={(value) => update({ primaryColor: value })}
              testId={`${testIdPrefix}-primary-color`}
              disabled={disabled}
            />
            <SingleColorField
              label="Accent color"
              value={draft.accentColor}
              fallback="#ff6600"
              onChange={(value) => update({ accentColor: value })}
              testId={`${testIdPrefix}-accent-color`}
              disabled={disabled}
            />
          </Stack>
          <ColorListField
            label="Secondary / neutral colors"
            value={draft.neutralColors}
            onChange={(value) => update({ neutralColors: value })}
            addLabel="Add neutral"
            fallback="#f5f5f5"
            testIdPrefix={`${testIdPrefix}-neutral-color`}
            disabled={disabled}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Primary font"
              value={draft.primaryFontName}
              onChange={(event) => update({ primaryFontName: event.target.value })}
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Headline style"
              value={draft.headingStyle}
              onChange={(event) => update({ headingStyle: event.target.value })}
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Body style"
              value={draft.bodyStyle}
              onChange={(event) => update({ bodyStyle: event.target.value })}
              fullWidth
              disabled={disabled}
            />
          </Stack>
        </Stack>
      </EditorSection>

      <EditorSection title="Composition & treatment">
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Grid"
              value={draft.compositionGrid}
              onChange={(event) => update({ compositionGrid: event.target.value })}
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Spacing"
              value={draft.compositionSpacing}
              onChange={(event) => update({ compositionSpacing: event.target.value })}
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Hierarchy"
              value={draft.compositionHierarchy}
              onChange={(event) => update({ compositionHierarchy: event.target.value })}
              fullWidth
              disabled={disabled}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <SelectField
              label="Image treatment"
              value={draft.imageTreatment}
              options={IMAGE_TREATMENT_OPTIONS}
              onChange={(value) => update({ imageTreatment: value })}
              disabled={disabled}
            />
            <TextField
              label="Line weight"
              value={draft.illustrationLineWeight}
              onChange={(event) => update({ illustrationLineWeight: event.target.value })}
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Shadow / depth"
              value={draft.illustrationShadow}
              onChange={(event) => update({ illustrationShadow: event.target.value })}
              fullWidth
              disabled={disabled}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Texture"
              value={draft.illustrationTexture}
              onChange={(event) => update({ illustrationTexture: event.target.value })}
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Photo camera"
              value={draft.photoCamera}
              onChange={(event) => update({ photoCamera: event.target.value })}
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Photo lighting"
              value={draft.photoLighting}
              onChange={(event) => update({ photoLighting: event.target.value })}
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="Photo background"
              value={draft.photoBackground}
              onChange={(event) => update({ photoBackground: event.target.value })}
              fullWidth
              disabled={disabled}
            />
          </Stack>
        </Stack>
      </EditorSection>

      <EditorSection title="Motifs, guardrails & text policy">
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Style keywords"
              multiline
              minRows={3}
              value={draft.moodKeywords}
              onChange={(event) => update({ moodKeywords: event.target.value })}
              fullWidth
              placeholder="One keyword per line"
              disabled={disabled}
            />
            <TextField
              label="Brand motifs"
              multiline
              minRows={3}
              value={draft.brandMotifs}
              onChange={(event) => update({ brandMotifs: event.target.value })}
              fullWidth
              placeholder="One repeated motif per line"
              disabled={disabled}
            />
            <TextField
              label="Forbidden visuals"
              multiline
              minRows={3}
              value={draft.forbiddenVisuals}
              onChange={(event) => update({ forbiddenVisuals: event.target.value })}
              fullWidth
              placeholder="One forbidden visual per line"
              disabled={disabled}
            />
          </Stack>
          <ColorListField
            label="Forbidden colors"
            value={draft.forbiddenColors}
            onChange={(value) => update({ forbiddenColors: value })}
            addLabel="Add forbidden"
            fallback="#ff0000"
            testIdPrefix={`${testIdPrefix}-forbidden-color`}
            disabled={disabled}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <SelectField
              label="Text policy"
              value={normalizeEditorTextPolicyMode(draft.textPolicyMode)}
              options={TEXT_POLICY_OPTIONS}
              onChange={(value) => update({ textPolicyMode: value })}
              disabled={disabled}
              sx={{ minWidth: 260, flex: '0 0 auto' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.exactTextRequired}
                  onChange={(event) => update({ exactTextRequired: event.target.checked })}
                  disabled={disabled}
                />
              }
              label="Exact rendered text required"
            />
            <SelectField
              label="Motion culture"
              value={draft.motionCulture}
              options={MOTION_OPTIONS}
              onChange={(value) => update({ motionCulture: value })}
              disabled={disabled}
              sx={{ minWidth: 180, flex: '0 0 auto' }}
            />
          </Stack>
        </Stack>
      </EditorSection>

      {showReviewed && (
        <EditorSection title="Metadata">
          <FormControlLabel
            control={
              <Switch
                checked={draft.isComplete}
                onChange={(event) => update({ isComplete: event.target.checked })}
                disabled={disabled}
              />
            }
            label="Reviewed"
          />
        </EditorSection>
      )}
      {showReferences && (
        <ReferenceSetImportDialog
          open={referenceSetOpen}
          profileKey={profileKey}
          platformId={platformId}
          remainingSlots={remainingReferenceSlots}
          importing={referenceSetImporting}
          onClose={() => setReferenceSetOpen(false)}
          onImport={importReferenceSet}
        />
      )}
    </Stack>
  );
};

function EditorSection({
  title,
  defaultExpanded = false,
  children,
}: {
  title: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      variant="outlined"
      sx={{
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 48,
          '& .MuiAccordionSummary-content': { my: 1 },
        }}
      >
        <Typography variant="subtitle2">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
  disabled,
  sx,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled?: boolean;
  sx?: object;
}) {
  return (
    <FormControl fullWidth sx={sx} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value}
        onChange={(event) => onChange(String(event.target.value))}
      >
        {options.map((option) => (
          <MenuItem key={option.value || 'inherit'} value={option.value}>{option.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function SingleColorField({
  label,
  value,
  fallback,
  onChange,
  testId,
  disabled,
}: {
  label: string;
  value: string;
  fallback: string;
  onChange: (value: string) => void;
  testId: string;
  disabled?: boolean;
}) {
  const displayValue = isValidHexColor(value) ? value : fallback;
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ColorInput
            value={displayValue}
            onChange={(next) => onChange(String(next))}
            testId={testId}
          />
        </Box>
        {value && (
          <Button size="small" onClick={() => onChange('')} disabled={disabled}>
            Clear
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function ColorListField({
  label,
  value,
  onChange,
  addLabel,
  fallback,
  testIdPrefix,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  addLabel: string;
  fallback: string;
  testIdPrefix: string;
  disabled?: boolean;
}) {
  const values = lines(value).filter(isValidHexColor);
  const commit = (nextValues: string[]) => onChange(nextValues.join('\n'));
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Button
          size="small"
          variant="text"
          startIcon={<AddIcon />}
          onClick={() => commit([...values, fallback])}
          disabled={disabled}
        >
          {addLabel}
        </Button>
      </Stack>
      <Stack spacing={0.75}>
        {values.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No colors set.
          </Typography>
        )}
        {values.map((color, index) => (
          <Stack key={`${color}-${index}`} direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ColorInput
                value={color}
                onChange={(next) => {
                  const updated = [...values];
                  updated[index] = String(next);
                  commit(updated);
                }}
                testId={`${testIdPrefix}-${index}`}
              />
            </Box>
            <IconButton
              aria-label={`Remove ${label.toLowerCase()} ${index + 1}`}
              onClick={() => commit(values.filter((_, colorIndex) => colorIndex !== index))}
              size="small"
              disabled={disabled}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function referencesFromProfile(profile: PlatformDesignProfile): BrandStyleKitReferenceDraft[] {
  const refs = (profile.visualReferences ?? [])
    .filter((ref) => ref.url)
    .map((ref, index) => {
      const role = (ref.role ?? defaultReferenceRole(String(profile.profileKey ?? 'design'), index)) as BrandStyleKitReferenceDraft['role'];
      return {
      url: ref.url,
      mimeType: ref.mimeType || inferBrandStyleKitMimeType(ref.url),
      name: ref.name ?? referenceSlotHint(String(profile.profileKey ?? 'design'), index),
      instruction: ref.instruction ?? defaultReferenceInstruction(String(profile.profileKey ?? 'design'), index, role),
      role,
      textBudgets: textBudgetDraftsFromReference(ref),
    };
    });
  if (refs.length > 0) return refs;

  const assets = ((profile.designSystem as any)?.brandAssets ?? {}) as {
    styleBoardUrls?: string[];
    logoUrls?: string[];
    productScreenshots?: string[];
  };
  return [
    ...(assets.styleBoardUrls ?? []).map((url, index) => ({
      url,
      mimeType: inferBrandStyleKitMimeType(url),
      name: index === 0 ? 'Core style board' : `Style board ${index + 1}`,
      instruction: 'Use mood, illustration/photo treatment, density, and palette relationships only; adapt colors to the current palette.',
      role: 'style' as const,
      textBudgets: [],
    })),
    ...(assets.logoUrls ?? []).map((url, index) => ({
      url,
      mimeType: inferBrandStyleKitMimeType(url),
      name: index === 0 ? 'Logo-safe brand cue' : `Logo reference ${index + 1}`,
      instruction: 'Use only for logo-safe area and brand recognition cues; exact logo rendering is overlay-safe.',
      role: 'logo' as const,
      textBudgets: [],
    })),
    ...(assets.productScreenshots ?? []).map((url, index) => ({
      url,
      mimeType: inferBrandStyleKitMimeType(url),
      name: index === 0 ? 'Approved product visual' : `Approved product visual ${index + 1}`,
      instruction: 'Use product shapes, UI patterns, and object identity as reference.',
      role: 'approved' as const,
      textBudgets: [],
    })),
  ];
}

function defaultReferenceDraft(profileKey: string, index: number): BrandStyleKitReferenceDraft {
  const role = defaultReferenceRole(profileKey, index);
  return {
    url: '',
    mimeType: 'image/png',
    name: referenceSlotHint(profileKey, index),
    instruction: defaultReferenceInstruction(profileKey, index, role),
    role,
    textBudgets: [],
  };
}

function textBudgetDraftsFromReference(ref: AiAssetReference): BrandStyleKitReferenceTextBudgetDraft[] {
  return (ref.textBudgets ?? []).map((budget) => {
    const canonical = canonicalReferenceTextBudgetSlot(budget.slot ?? '');
    return {
      slot: canonical.slot,
      textRole: budget.textRole ?? canonical.textRole ?? '',
      containerSlot: budget.containerSlot ?? canonical.containerSlot ?? '',
      label: budget.label ?? '',
      recommendedChars: integerToDraftString(budget.recommendedChars),
      maxChars: integerToDraftString(budget.maxChars),
      maxLines: integerToDraftString(budget.maxLines),
      notes: budget.notes ?? '',
    };
  });
}

function textBudgetDraftsToReference(
  budgets: BrandStyleKitReferenceTextBudgetDraft[],
): NonNullable<AiAssetReference['textBudgets']> | undefined {
  const normalized = budgets
    .map((budget) => {
      const slot = budget.slot.trim();
      const maxChars = parseNonNegativeInteger(budget.maxChars);
      if (!slot || maxChars === undefined) return undefined;
      const canonical = canonicalReferenceTextBudgetSlot(slot);
      return {
        slot: canonical.slot,
        textRole: budget.textRole.trim() || canonical.textRole || undefined,
        containerSlot: budget.containerSlot.trim() || canonical.containerSlot || undefined,
        label: budget.label.trim() || undefined,
        recommendedChars: parseNonNegativeInteger(budget.recommendedChars),
        maxChars,
        maxLines: parseNonNegativeInteger(budget.maxLines),
        notes: budget.notes.trim() || undefined,
      };
    })
    .filter((budget): budget is NonNullable<typeof budget> => Boolean(budget));
  return normalized.length > 0 ? normalized : undefined;
}

function defaultReferenceTextBudgetDraft(index: number): BrandStyleKitReferenceTextBudgetDraft {
  const defaults = [
    { slot: 'hero_title', textRole: 'title', containerSlot: 'hero_text_block', label: 'Hero title', maxChars: '64', maxLines: '2' },
    { slot: 'hero_subtitle', textRole: 'subtitle', containerSlot: 'hero_text_block', label: 'Hero subtitle', maxChars: '80', maxLines: '2' },
    { slot: 'body_text', textRole: 'body', containerSlot: 'body_text_block', label: 'Body text', maxChars: '160', maxLines: '4' },
    { slot: 'footer_cta', textRole: 'cta', containerSlot: 'footer_action_block', label: 'Footer CTA', maxChars: '32', maxLines: '1' },
    { slot: 'badge', textRole: 'badge', containerSlot: 'badge_block', label: 'Badge', maxChars: '24', maxLines: '1' },
  ];
  const preset = defaults[index] ?? {
    slot: 'caption',
    textRole: 'caption',
    containerSlot: 'caption_block',
    label: 'Caption',
    maxChars: '80',
    maxLines: '2',
  };
  return {
    slot: preset.slot,
    textRole: preset.textRole,
    containerSlot: preset.containerSlot,
    label: preset.label,
    recommendedChars: '',
    maxChars: preset.maxChars,
    maxLines: preset.maxLines,
    notes: '',
  };
}

const REFERENCE_TEXT_BUDGET_SLOT_DEFAULTS: Record<string, { slot?: string; textRole?: string; containerSlot?: string }> = {
  headline: { slot: 'hero_title', textRole: 'title', containerSlot: 'hero_text_block' },
  title: { slot: 'hero_title', textRole: 'title', containerSlot: 'hero_text_block' },
  hero_title: { textRole: 'title', containerSlot: 'hero_text_block' },
  subtitle: { slot: 'hero_subtitle', textRole: 'subtitle', containerSlot: 'hero_text_block' },
  hero_subtitle: { textRole: 'subtitle', containerSlot: 'hero_text_block' },
  body: { slot: 'body_text', textRole: 'body', containerSlot: 'body_text_block' },
  body_text: { textRole: 'body', containerSlot: 'body_text_block' },
  footer_cta: { textRole: 'cta', containerSlot: 'footer_action_block' },
  cta: { slot: 'footer_cta', textRole: 'cta', containerSlot: 'footer_action_block' },
  step_label: { textRole: 'eyebrow', containerSlot: 'step_marker_block' },
  badge: { textRole: 'badge', containerSlot: 'badge_block' },
  caption: { textRole: 'caption', containerSlot: 'caption_block' },
};

function canonicalReferenceTextBudgetSlot(slot: string): { slot: string; textRole?: string; containerSlot?: string } {
  const key = slot.trim().toLowerCase();
  const defaults = REFERENCE_TEXT_BUDGET_SLOT_DEFAULTS[key];
  return {
    slot: defaults?.slot || slot,
    textRole: defaults?.textRole,
    containerSlot: defaults?.containerSlot,
  };
}

function integerToDraftString(value: unknown): string {
  return Number.isInteger(value) && Number(value) >= 0 ? String(value) : '';
}

function parseNonNegativeInteger(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) return undefined;
  return parsed;
}

export function defaultReferenceRole(profileKey: string, index: number): BrandStyleKitReferenceDraft['role'] {
  if (!profileKey.startsWith('carousel')) return index === 0 ? 'style' : 'layout';
  if (index === 0) return 'layout';
  if (index === 1) return 'layout';
  return 'approved';
}

function referenceGuidance(profileKey: string): string {
  if (profileKey.startsWith('carousel')) {
    return 'Recommended order: cover or hook slide, body slide layout, CTA/end slide treatment, then optional approved style examples.';
  }
  if (profileKey.startsWith('single_image')) {
    return 'Recommended order: composition/style reference, typography reference, then optional approved final image examples.';
  }
  return 'Recommended order: core style board, typography or layout system, logo, then approved examples.';
}

export function referenceSlotHint(profileKey: string, index: number): string {
  if (profileKey.startsWith('carousel')) {
    if (index === 0) return 'Cover / hook';
    if (index === 1) return 'Body slide';
    if (index === 2) return 'CTA slide';
  }
  if (profileKey.startsWith('single_image')) {
    if (index === 0) return 'Composition';
    if (index === 1) return 'Typography';
  }
  if (index === 0) return 'Style board';
  if (index === 1) return 'Layout / type';
  return 'Reference';
}

export function defaultReferenceInstruction(
  profileKey: string,
  index: number,
  role: BrandStyleKitReferenceDraft['role'] = defaultReferenceRole(profileKey, index),
): string {
  const usage = profileKey.startsWith('carousel')
    ? carouselReferenceUsage(index)
    : singleImageReferenceUsage(index);
  return `${usage} ${instructionForRole(role)}`.trim();
}

function carouselReferenceUsage(index: number): string {
  if (index === 0) return 'Use as hook/cover slide art-direction guidance.';
  if (index === 1) return 'Use as body slide layout and rhythm guidance.';
  if (index === 2) return 'Use as CTA/end slide treatment guidance.';
  return 'Use as supporting series style guidance.';
}

function singleImageReferenceUsage(index: number): string {
  if (index === 0) return 'Use as single-image art-direction guidance.';
  if (index === 1) return 'Use as supporting layout or motif guidance.';
  return 'Use as supporting style guidance.';
}

function instructionForRole(role: BrandStyleKitReferenceDraft['role']): string {
  switch (role) {
    case 'layout':
      return 'Use layout, spacing, and hierarchy only; adapt colors to the current palette.';
    case 'typography':
      return 'Use typography feel and text rhythm only; exact text is overlay-safe.';
    case 'logo':
      return 'Use brand recognition and logo-safe area only; exact logo should be overlaid.';
    case 'approved':
      return 'Use as approved post/package example for style consistency; adapt colors to the current palette.';
    case 'anchor':
      return 'Use only when explicitly selected as the global style anchor; preserve visual language without duplicating composition, scene, text, or source colors.';
    case 'motif':
      return 'Use repeated motif and visual language only; adapt colors to the current palette.';
    case 'object':
      return 'Use object/product identity and visual proportions.';
    case 'character':
      return 'Use subject identity and character consistency.';
    default:
      return 'Use overall mood, visual density, and art direction only; adapt colors to the current palette.';
  }
}

function lines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function stripEmptyObject<T extends Record<string, any>>(value: T): T | undefined {
  const cleaned = Object.entries(value).reduce<Record<string, any>>((acc, [key, entry]) => {
    if (entry === undefined || entry === null) return acc;
    if (Array.isArray(entry)) {
      if (entry.length > 0) acc[key] = entry;
      return acc;
    }
    if (typeof entry === 'object') {
      const child = stripEmptyObject(entry);
      if (child) acc[key] = child;
      return acc;
    }
    if (entry !== '') acc[key] = entry;
    return acc;
  }, {});
  return Object.keys(cleaned).length > 0 ? cleaned as T : undefined;
}
